// TODO: Fix all linting warnings in layout-lib #36010
/* eslint-disable */
import { Router, RoutesRecognized } from '@angular/router';

import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Menu } from './menu.model';
import { RenderableMenu } from './renderable-menu.model';

export class MenuService {
  private _menu: RenderableMenu[];

  private _map: { [identifier: string]: RenderableMenu } = {};

  private _url: BehaviorSubject<string> = new BehaviorSubject<string>('/');

  /**
   * Gets the full menu tree.
   *
   * Can be observed for lazy loaded changes.
   */
  public get menu(): RenderableMenu[] {
    return this._menu;
  }

  constructor(
    readonly config: Menu[],
    readonly router: Router
  ) {
    this._menu = this.toRenderables(config, null);
    router.events
      .pipe(
        filter((event) => event instanceof RoutesRecognized),
        map((event: RoutesRecognized) => event.urlAfterRedirects)
      )
      .subscribe(this._url);
  }

  get url(): Observable<string> {
    return this._url;
  }

  public setChildren(menuIdentifier: string, menu: Menu[]) {
    // Find parent in renderable menus
    const parentRenderable = this._map[menuIdentifier];
    const renderables = this.toRenderables(menu, parentRenderable.routerLink);
    parentRenderable.loadChildren(renderables);
  }

  public setLazyChildren(menuIdentifier: string, menus$: Observable<Menu[]>) {
    // Find parent in renderable menus
    const parentRenderable = this._map[menuIdentifier];
    const lazyRenderables$: Observable<RenderableMenu[]> = menus$.pipe(
      map((menus) => this.toRenderables(menus, parentRenderable.routerLink))
    );

    if (!parentRenderable) {
      console.warn(
        `MenuService could not load lazy children, as the parent menu for menu '${menuIdentifier}' was not found!`
      );
      return;
    }

    parentRenderable.loadLazyChildren(lazyRenderables$);
  }

  public toRenderables(menus: Menu[], parentRouterLink: any[]): RenderableMenu[] {
    return menus.map((menu) => {
      const renderable = new RenderableMenu(this.router, menu, parentRouterLink, this.url);
      if (menu.children) {
        // Eagerly insert non-lazy loaded children
        renderable.loadChildren(this.toRenderables(menu.children, renderable.routerLink));
      } else if (menu.loadChildren) {
        // Map the menu item to the renderable for efficient lookup
        this._map[this.getMenuIdentifier(menu)] = renderable;
      }
      return renderable;
    });
  }

  /**
   * Gets an uniquely generated identifier for the given Menu, meant for Menu's at the first level.
   * This identifier is based on the routerLink commands with support of primitive types and objects for params and outlets.
   */
  public getMenuIdentifier(menu: Menu): string {
    const getRouterLinkCommandIdentifier = (routerLinkCommand: any): string => {
      if (typeof routerLinkCommand !== 'object') {
        return `${routerLinkCommand}`;
      }

      if (routerLinkCommand.outlets) {
        return Object.keys(routerLinkCommand.outlets).map((x) => `${x}:${routerLinkCommand.outlets[x]}`)[0];
      } else {
        return Object.keys(routerLinkCommand).map((x) => `${x}:${routerLinkCommand[x]}`)[0];
      }
    };

    return typeof menu.routerLink === 'object'
      ? menu.routerLink.map((command) => getRouterLinkCommandIdentifier(command)).join('::')
      : `${menu.routerLink}`;
  }
}
